import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView } from '@/components/base'
import { SliderItem } from './components/SliderItem'
import { SkeletonCard } from '../Wallet/components/SkeletonCard'
import { homeSliders, HomeSlidersEnumID } from '@/constants/homeSliders'
import { useGetFeaturesSettings, useGetWallet, UserProfile } from '@/domain'
import { extractPhoneNumberInfosWithoutRegionCode } from '@/utils/phoneNumber/phoneNumberUtils'

interface HomePageSlidersProps {
  skeletonActive?: boolean
  userAuthenticated?:
    | (UserProfile & {
        fullName: string
        hasIdentityVerified: boolean
        document_country: string
      })
    | null
}

export const HomePageSliders: React.FC<HomePageSlidersProps> = ({
  skeletonActive = false,
  userAuthenticated,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: walletData, isLoading: isLoadingWallet } = useGetWallet()

  // Determine if user has cards - only true when data is loaded AND cards exist
  // While loading, treat as "has cards" to prevent showing the banner prematurely
  const hasCards = isLoadingWallet
    ? true // Hide banner while loading
    : Boolean(walletData?.cards?.length && walletData?.cards?.length > 0)

  // Real user data
  const { data: featureSettings } = useGetFeaturesSettings()

  const phoneRequired = useMemo(() => {
    if (!featureSettings?.features) return false

    // Check all feature categories
    for (const featureKey in featureSettings.features) {
      const feature =
        featureSettings?.features[
          featureKey as keyof typeof featureSettings.features
        ]

      // Check if feature has actions
      if (feature.actions && Array.isArray(feature.actions)) {
        // Look for phone requirement in actions
        const phoneAction = feature.actions.find(
          (action: { data_type: string; required: boolean }) => {
            return action.data_type === 'phone' && action.required === true
          },
        )

        if (phoneAction) {
          return true
        } else {
          return false
        }
      }
    }

    return featureSettings?.isPhoneRequired || false
  }, [featureSettings])

  const rewardsLimit = featureSettings?.reward_limit_formatted
  const rewardsEnabled = featureSettings?.rewards?.enabled
  const userDocumentCountry = userAuthenticated?.document_country
  const rewardsAllowedCountries = featureSettings?.rewards?.allowed_countries
  const isRewardsAllowedCountry = rewardsAllowedCountries?.includes(
    userDocumentCountry || '',
  )
  const userPhoneNumber = userAuthenticated?.phone_number
  const userPhoneNumberInfos = userPhoneNumber
    ? extractPhoneNumberInfosWithoutRegionCode(userPhoneNumber)
    : null
  const isRewardsAllowedCountryByPhone = userPhoneNumberInfos?.iso3Code
    ? rewardsAllowedCountries?.includes(userPhoneNumberInfos?.iso3Code || '')
    : false
  const cashbackInfo = featureSettings?.cashback?.cashback_programs?.[0]
  const userData = useMemo(
    () => ({
      hasVerifiedEmail: userAuthenticated?.email_confirmed || false,
      hasIdentityVerified: userAuthenticated?.hasIdentityVerified || false,
      hasPhoneNumber: Boolean(userAuthenticated?.phone_number),
      hasCards,
      isLoadingWallet,
      shouldDisplayReferralBonuses: rewardsEnabled, // TODO: get from feature flags - COMMENTED OUT: Invite Friends widget
      rewardLimitFormatted: rewardsLimit, // TODO: get from referral settings
    }),
    [
      userAuthenticated,
      hasCards,
      isLoadingWallet,
      rewardsEnabled,
      rewardsLimit,
    ],
  )

  // Filter sliders based on user state (like in original)
  const filteredHomeSliders = useMemo(() => {
    return homeSliders.filter(({ id }) => {
      if (id === HomeSlidersEnumID.addPhoneNumber && !phoneRequired) {
        return false
      }

      if (id === HomeSlidersEnumID.verifyEmail && userData.hasVerifiedEmail) {
        return false
      }

      if (id === HomeSlidersEnumID.addPhoneNumber && userData.hasPhoneNumber) {
        return false
      }

      if (id === HomeSlidersEnumID.verifyID && userData.hasIdentityVerified) {
        return false
      }

      if (
        id === HomeSlidersEnumID.inviteFriend &&
        ((!userData.shouldDisplayReferralBonuses && !rewardsEnabled) ||
          !isRewardsAllowedCountry ||
          !isRewardsAllowedCountryByPhone)
      ) {
        // console.log('HomeSlidersEnumID.cashback && userData.a')

        return false
      }

      if (
        (id === HomeSlidersEnumID.cashback && userData.hasCards) ||
        !cashbackInfo
      ) {
        // console.log(cashbackInfo)
        // console.log('HomeSlidersEnumID.cashback && userData.hasCards')
        return false
      }
      // console.log(cashbackInfo)
      return true
    })
  }, [
    userData.hasVerifiedEmail,
    userData.hasIdentityVerified,
    userData.hasPhoneNumber,
    userData.shouldDisplayReferralBonuses,
    userData.hasCards,
  ])
  // Navigation mapping (like in original)
  const sliderRoutesMap = {
    [HomeSlidersEnumID.verifyEmail]: '/check-your-email',
    [HomeSlidersEnumID.verifyID]: '/check-your-identity',
    [HomeSlidersEnumID.addPhoneNumber]: '/add-phone',
    [HomeSlidersEnumID.inviteFriend]: '/invite-friends',
    [HomeSlidersEnumID.cashback]: '/cashback-info',
  }

  const handleSliderPress = (sliderId: HomeSlidersEnumID) => {
    const route = sliderRoutesMap[sliderId]
    if (route) {
      navigate(route)
    }
  }

  if (skeletonActive) {
    return <SkeletonCard height={115} radius={32} align="center" />
  }

  if (filteredHomeSliders.length === 0) {
    return null
  }

  return (
    <BaseView>
      {/* Horizontal scrollable container */}
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          gap: '12px',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
        }}
        className="hide-scrollbar"
      >
        {filteredHomeSliders.map((slider) => {
          let adaptedTitle = t(slider.title)
          let adaptedDescription = slider.description
            ? t(slider.description)
            : undefined

          // Handle inviteFriend title with formattedAmount
          if (
            slider.id === HomeSlidersEnumID.inviteFriend &&
            userData.rewardLimitFormatted
          ) {
            adaptedTitle = t(slider.title, {
              formattedAmount: userData.rewardLimitFormatted,
            })
          }

          // Handle cashback title and description with cryptoName and percentage
          if (slider.id === HomeSlidersEnumID.cashback && cashbackInfo) {
            adaptedTitle = t(slider.title, {
              cryptoName: cashbackInfo.name,
              percentage: `${cashbackInfo.percentage}%`,
            })
            adaptedDescription = slider.description
              ? t(slider.description, {
                  percentage: `${cashbackInfo.percentage}%`,
                })
              : undefined
          }

          return (
            <SliderItem
              key={slider.id}
              id={slider.id}
              title={adaptedTitle}
              titleFontSize={slider.titleFontSize}
              description={
                slider.id !== HomeSlidersEnumID.inviteFriend
                  ? adaptedDescription
                  : ''
              }
              descriptionFontSize={slider.descriptionFontSize}
              backgroundImage={slider.backgroundImage}
              internalImage={slider.internalImage}
              descriptionIconName={
                slider.id !== HomeSlidersEnumID.inviteFriend
                  ? slider.descriptionIconName
                  : ''
              }
              maxContentWidth={slider.maxContentWidth}
              onPress={() => handleSliderPress(slider.id)}
            />
          )
        })}
      </div>

      {/* Hide scrollbar styles */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </BaseView>
  )
}
